import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import {
    Box,
    Typography,
    Grid,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { useTranslation } from 'react-i18next';
import ContentArea from '../../components/contentArea';
import Button from '../../components/button';
import InputField from '../../components/formItem/inputField';
import { InputFieldComponent } from '../../components/formItem';
import Dropdown from '../../components/dropdown';
import CommonCard from '../../components/card/CommonCard';
import { eventAPI } from '../../actions/imsServer';
import { EVENT_TYPES } from '../../constants';
import { formatDateForEventMySQL, formatDateTimeForInput } from '../../utils/base';
import dayjs from 'dayjs';

const EventEdit = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [, setMeInfo] = useState(null);
    const theme = useTheme();

    // 편집 모드인지 확인 (location.state에 eventData가 있으면 편집 모드)
    const eventData = location.state?.eventData;
    const isEditMode = !!eventData;

    useEffect(() => {
        const meInfo = JSON.parse(localStorage.getItem('meInfo') || '{}');
        setMeInfo(meInfo);
    }, []);

    const [form, setForm] = useState({
        type: 0, // 이벤트 타입 (0: 추첨, 1: 랭킹, 2: 일반)
        name: '', // 이벤트 명칭
        targetCount: '', // 당첨자(기관)수
        startDate: '', // 응모게시일
        endDate: '', // 응모마감일
    });

    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [saving, setSaving] = useState(false);

    // 편집 모드일 때 기존 데이터 로드
    useEffect(() => {
        if (isEditMode && eventData) {
            const eventType = eventData.type !== undefined ? eventData.type : 0;
            let targetCount = '';
            if (eventType === 2) {
                // 일반: '-' 표기
                targetCount = '-';
            } else {
                // 추첨제 또는 랭킹제: 기존 값 또는 기본값
                targetCount = eventData.targetCount !== undefined && eventData.targetCount !== null
                    ? String(eventData.targetCount)
                    : (eventType === 0 ? '5' : '1');
            }

            setForm({
                type: eventType,
                name: eventData.name || '',
                targetCount: targetCount,
                startDate: eventData.startDate ? formatDateTimeForInput(eventData.startDate) : '',
                endDate: eventData.endDate ? formatDateTimeForInput(eventData.endDate) : '',
            });
            // 이미지가 있으면 미리보기 설정
            if (eventData.file || eventData.image || eventData.imageUrl) {
                setImagePreview(eventData.file || eventData.image || eventData.imageUrl);
            }
        } else {
            // 추가 모드: 타입에 따라 기본값 설정
            setForm(prev => ({
                ...prev,
                targetCount: prev.type === 0 ? '5' : prev.type === 1 ? '1' : '-'
            }));
        }
    }, [isEditMode, eventData]);

    const setField = (key, value) => {
        setForm(prev => {
            const newForm = { ...prev, [key]: value };
            // 이벤트 타입이 변경될 때 당첨자(기관)수 자동 설정
            if (key === 'type') {
                if (value === 0) {
                    // 추첨제: 기본값 5
                    newForm.targetCount = '5';
                } else if (value === 1) {
                    // 랭킹제: 기본값 1
                    newForm.targetCount = '1';
                } else if (value === 2) {
                    // 일반: '-' 표기
                    newForm.targetCount = '-';
                }
            }
            return newForm;
        });
    };

    // 이미지 파일 선택 핸들러
    const handleImageSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            // 이미지 파일인지 확인
            if (!file.type.startsWith('image/')) {
                alert(t('only_image_files_allowed'));
                return;
            }
            // 파일 크기 제한 (10MB)
            if (file.size > 10 * 1024 * 1024) {
                alert(t('file_size_max_10mb'));
                return;
            }
            setImageFile(file);
            // 미리보기 생성
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    // 이미지 제거 핸들러
    const handleImageRemove = () => {
        setImageFile(null);
        setImagePreview(null);
    };

    const isValid = () => {
        if (!form.name || form.name.trim() === '') return false;
        // 일반(type === 2)이 아닐 때만 당첨자(기관)수 검증
        if (form.type !== 2) {
            if (!form.targetCount || form.targetCount === '-' || isNaN(Number(form.targetCount)) || Number(form.targetCount) <= 0) return false;
        }
        if (!form.startDate) return false;
        if (!form.endDate) return false;

        // 이미지 필수 검증
        if (!imageFile && !imagePreview) return false;

        const startDate = dayjs(form.startDate).startOf('day');
        const endDate = dayjs(form.endDate).startOf('day');

        // 마감일은 게시일보다 빠를 수 없음
        if (endDate.isBefore(startDate)) return false;

        // 편집 모드가 아닐 때만 날짜가 오늘 이후인지 검증
        if (!isEditMode) {
            const today = dayjs().startOf('day');
            // 게시일은 오늘보다 과거일 수 없음
            if (startDate.isBefore(today)) return false;
            // 마감일은 오늘보다 과거일 수 없음
            if (endDate.isBefore(today)) return false;
        }

        return true;
    };

    const handleCancel = () => {
        navigate('/events/admin');
    };

    const handleSubmit = async () => {
        if (!isValid()) {
            if (!imageFile && !imagePreview) {
                alert(t('please_attach_post_image'));
            } else {
                alert(t('please_fill_required_fields'));
            }
            return;
        }
        setSaving(true);
        try {
            const eventDataToSave = {
                name: form.name,
                type: form.type,
                startDate: formatDateForEventMySQL(form.startDate, false),
                endDate: formatDateForEventMySQL(form.endDate, true),
                targetCount: form.type === 2 ? 0 : Number(form.targetCount), // 일반일 때는 0
            };

            // 이미지 파일이 있으면 추가
            if (imageFile) {
                eventDataToSave.file = imageFile;
            }

            // 편집 모드인 경우 id 추가
            if (isEditMode && eventData?.id) {
                eventDataToSave.id = eventData.id;
                await eventAPI.updateEvent(eventDataToSave);
            } else {
                await eventAPI.registerEvent(eventDataToSave);
            }

            navigate('/events/admin');
        } catch (e) {
            console.error('이벤트 저장 실패:', e);
            alert(t('event_save_failed'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <ContentArea padding='20px 10px 10px 10px'>
            <Box sx={{ px: 3, pb: 4, width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Grid container spacing={4} sx={{ flex: 1 }}>
                    {/* 왼쪽 컬럼 */}
                    <Grid item xs={12} md={6} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <CommonCard sx={{ alignItems: 'stretch', gap: 3 }}>
                            <InputFieldComponent title={`${t('event_type')} *`}>
                                <Dropdown
                                    options={EVENT_TYPES}
                                    value={form.type}
                                    onChange={(opt) => setField('type', opt.value)}
                                    placeholder={t('please_select')}
                                />
                            </InputFieldComponent>

                            <InputFieldComponent title={`${t('event_name')} *`}>
                                <InputField
                                    placeholder={t('please_enter')}
                                    value={form.name}
                                    onChange={(e) => setField('name', e.target.value)}
                                />
                            </InputFieldComponent>

                            <InputFieldComponent title={`${t('event_winner_count')} *`}>
                                <InputField
                                    type="number"
                                    placeholder={form.type === 2 ? '-' : t('please_enter')}
                                    value={form.targetCount}
                                    onChange={(e) => {
                                        if (form.type !== 2) {
                                            setField('targetCount', e.target.value);
                                        }
                                    }}
                                    disabled={form.type === 2}
                                />
                            </InputFieldComponent>

                            <InputFieldComponent title={`${t('application_start_date')} *`}>
                                <InputField
                                    type="date"
                                    placeholder="YYYY-MM-DD"
                                    value={form.startDate}
                                    onChange={(e) => setField('startDate', e.target.value)}
                                    inputProps={{
                                        min: dayjs().format('YYYY-MM-DD') // 오늘 이후만 선택 가능
                                    }}
                                />
                            </InputFieldComponent>

                            <InputFieldComponent title={`${t('application_end_date')} *`}>
                                <InputField
                                    type="date"
                                    placeholder="YYYY-MM-DD"
                                    value={form.endDate}
                                    onChange={(e) => setField('endDate', e.target.value)}
                                    inputProps={{
                                        min: (() => {
                                            const today = dayjs().format('YYYY-MM-DD');
                                            if (form.startDate) {
                                                const startDate = dayjs(form.startDate).format('YYYY-MM-DD');
                                                // 게시일과 오늘 중 더 늦은 날짜 반환
                                                return dayjs(startDate).isAfter(dayjs(today)) ? startDate : today;
                                            }
                                            return today;
                                        })()
                                    }}
                                />
                            </InputFieldComponent>
                        </CommonCard>
                    </Grid>

                    {/* 오른쪽 컬럼 */}
                    <Grid item xs={12} md={6} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <CommonCard sx={{ alignItems: 'stretch', gap: 3 }}>
                            <InputFieldComponent title={`${t('attach_post_image')} *`} sx={{ height: 'auto' }}>
                                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'row', gap: 1 }}>

                                    <Box
                                        sx={{
                                            width: '340px',
                                            minHeight: '243px',
                                            bgcolor: theme.palette.figma.white[10],
                                            borderRadius: '8px',
                                            border: '1px solid ' + theme.palette.figma.white[10],
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            position: 'relative',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        {imagePreview ? (
                                            <Box
                                                component="img"
                                                src={imagePreview}
                                                alt={t('image_preview')}
                                                sx={{
                                                    maxWidth: '100%',
                                                    maxHeight: '243px',
                                                    objectFit: 'contain',
                                                }}
                                            />
                                        ) : (
                                            <>
                                                <Box
                                                    component="input"
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={handleImageSelect}
                                                    sx={{ display: 'none' }}
                                                    id="image-upload"
                                                />
                                                <Box
                                                    component="label"
                                                    htmlFor="image-upload"
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        cursor: 'pointer',
                                                        width: '100%',
                                                        height: '100%',
                                                        minHeight: '243px',
                                                    }}
                                                >
                                                    <Typography sx={{
                                                        fontSize: '14px',
                                                        color: theme.palette.figma.white[60],
                                                        mb: 1,
                                                    }}>
                                                        {t('width_340px_fixed_height_min_243px')}
                                                    </Typography>
                                                </Box>
                                            </>
                                        )}
                                    </Box>
                                    {imagePreview && (

                                        <Button
                                            variant="gray"
                                            onClick={handleImageRemove}
                                            sx={{
                                                minWidth: '60px',
                                            }}
                                        >
                                            {t('delete')}
                                        </Button>

                                    )}
                                </Box>
                            </InputFieldComponent>
                        </CommonCard>
                    </Grid>
                </Grid>

                {/* 하단 버튼 */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'flex-end',
                    gap: 2,
                    px: 2.5,
                    pb: 3,
                    alignSelf: 'stretch',
                    marginTop: 'auto'
                }}>
                    <Button variant="gray" onClick={handleCancel} sx={{ minWidth: '120px' }}>{t('cancel')}</Button>
                    <Button onClick={handleSubmit} disabled={saving || !isValid()} sx={{ minWidth: '120px' }}>{t('save_button')}</Button>
                </Box>
            </Box>
        </ContentArea>
    );
};

export { EventEdit };
